import { ReactNode, useState } from 'react';
import { createZodFormResolver, lt, useFeatureFlag, useFormControls } from '@mews/framework';
import { WithScope } from '@mews-commander/types';
import { FeatureFlag, SearchType } from '@mews-commander/enums';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { SearchHookField } from '../../../../../components/form/hookFields';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { getCheckedReservationIds } from '../../../selectors';
import { GroupMergeFormKeys, groupMergeFormSchema, GroupMergeFormValues } from './types';
import { useChangeGroupMergeMutation } from './useChangeGroupMergeMutation';

const EMPTY = '';

export const GroupMergeForm = ({ scope, onSuccess }: WithScope & { onSuccess?: () => void }) => {
    const { mutate, isPending } = useChangeGroupMergeMutation({
        groupId: scope,
    });
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const handleSubmit = (values: GroupMergeFormValues) => {
        const result = validate();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        mutate(values, {
            onSuccess: () => {
                onSuccess?.();
                reset();
            },
        });
    };

    const { renderFormContent, reset, watch, hasDirtyFields } = useFormControls({
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(groupMergeFormSchema),
        defaultValues: {
            [GroupMergeFormKeys.sourceGroupId]: EMPTY,
        },
        dataTestForm: 'group-merge-form',
    });

    const reservationIds = useCommanderSelector((state) => getCheckedReservationIds(state, { scope }));

    const sourceGroupId = watch(GroupMergeFormKeys.sourceGroupId);
    const gainsightEvents = useGainsightCommanderEvents();

    const { validate, isLoading } = useRoutingRuleChangesConfirmation({
        reservationIds,
        groupId: sourceGroupId,
        enabled: granularRoutingRulesConditionsExtensionEnabled && hasDirtyFields,
        scope,
        onSubmit: ({ changes }) => {
            const onInternalSuccess = () => {
                gainsightEvents.onApiSuccess({
                    context: 'BillingAutomationChanged',
                    data: {
                        changes,
                        origin: 'MergeGroup',
                    },
                });
                onSuccess?.();
                reset();
            };
            mutate(watch(), { onSuccess: onInternalSuccess });
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => {
            reset();
            setRoutingRuleChangesDialog(null);
        },
    });

    return (
        <>
            {renderFormContent(
                <FormLayout
                    vertical
                    loading={isPending}
                    submitButtonLoading={isLoading}
                    submitButtonText={lt('Merge')}
                    title={lt('MergeGroupIntoThis')}
                >
                    <SearchHookField
                        data-test-id={GroupMergeFormKeys.sourceGroupId}
                        newEntity={false}
                        name={GroupMergeFormKeys.sourceGroupId}
                        label={lt('ReservationGroupName')}
                        searchTypes={[SearchType.ReservationGroup]}
                        except={[scope]} // Exclude the current group from the search
                    />
                </FormLayout>
            )}
            {routingRuleChangesDialog}
        </>
    );
};
